
import { GAME_ART, elem, styleInstaller } from "./chrome";

export const ENERGYBAR_WIDTH = 50;
export const ENERGYBAR_HEIGHT = 10;
export const ENERGYBAR_BOTTOM = 55;
export const ENERGY_DURATION_SECONDS = 7200;
export const ENERGY_HUNGRY_PERCENT = 15;

export type EnergyPhase = "empty" | "hungry" | "full";

const STYLE_ID = "energybar-style";
const ART = `${GAME_ART}/energygauge`;
const STYLE = `
.energybar {
  position: fixed;
  left: 0;
  bottom: ${ENERGYBAR_BOTTOM}px;
  /* Under the game buttons: built by __MakeTaskBar (interfacemodule.py:144)
     before __MakeGameButtonWindow (:290-291), and never raised. */
  z-index: 38;
  width: ${ENERGYBAR_WIDTH}px;
  height: ${ENERGYBAR_HEIGHT}px;
  pointer-events: auto;
}
.energybar-board {
  position: absolute;
  left: 0;
  top: -10px;
  width: 68px;
  height: 40px;
  background: url("${ART}/energygauge_base.png") no-repeat;
  pointer-events: none;
}
.energybar-gauge {
  position: absolute;
  left: 5px;
  top: 8px;
  width: 46px;
  height: 17px;
  pointer-events: none;
}
.energybar-gauge[hidden] { display: none; }
.energybar-empty { background: url("${ART}/gauge_empty.png") no-repeat; }
.energybar-hungry { background: url("${ART}/gauge_hungry.png") no-repeat; }
.energybar-full { background: url("${ART}/gauge_full.png") no-repeat; }
.energybar-tooltip-hit {
  position: absolute;
  left: 0;
  top: 0;
  width: ${ENERGYBAR_WIDTH}px;
  height: ${ENERGYBAR_HEIGHT}px;
}
.energybar-tip {
  position: fixed;
  z-index: 50;
  padding: 2px 4px;
  color: #f3e7c1;
  background: rgba(0, 0, 0, .72);
  border: 1px solid rgba(243, 231, 193, .24);
  font: 11px/1 system-ui, sans-serif;
  pointer-events: none;
  white-space: nowrap;
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export function energyPhase(_energy: number, remainingSeconds: number): EnergyPhase {
  const left = Math.max(0, remainingSeconds);
  if (left === 0) return "empty";
  if ((left * 100) / ENERGY_DURATION_SECONDS < ENERGY_HUNGRY_PERCENT) return "hungry";
  return "full";
}

export function energyTooltip(energy: number): string {
  return `Energy ${Math.max(0, Math.floor(energy))}`;
}

export class EnergyBar {
  readonly element: HTMLElement;
  private readonly empty: HTMLElement;
  private readonly hungry: HTMLElement;
  private readonly full: HTMLElement;
  private readonly tip: HTMLElement;
  private energy = 0;
  private remaining = 0;

  constructor(mount: HTMLElement) {
    const doc = mount.ownerDocument;
    installStyle(doc);
    this.element = elem(doc, "div", "energybar");
    this.element.setAttribute("role", "meter");
    this.element.setAttribute("aria-label", "Energy");
    const board = elem(doc, "div", "energybar-board");
    this.empty = elem(doc, "span", "energybar-gauge energybar-empty");
    this.hungry = elem(doc, "span", "energybar-gauge energybar-hungry");
    this.hungry.hidden = true;
    this.full = elem(doc, "span", "energybar-gauge energybar-full");
    this.full.hidden = true;
    board.append(this.empty, this.hungry, this.full);
    const hit = elem(doc, "div", "energybar-tooltip-hit");
    this.tip = elem(doc, "div", "energybar-tip");
    this.tip.hidden = true;
    hit.addEventListener("mouseenter", (event) => this.showTip(event));
    hit.addEventListener("mousemove", (event) => this.showTip(event));
    hit.addEventListener("mouseleave", () => {
      this.tip.hidden = true;
    });
    this.element.append(board, hit, this.tip);
    mount.append(this.element);
    this.update(0, 0);
  }

  update(energy: number, remainingSeconds: number): void {
    this.energy = Number.isFinite(energy) ? energy : 0;
    this.remaining = Number.isFinite(remainingSeconds) ? remainingSeconds : 0;
    const phase = energyPhase(this.energy, this.remaining);
    this.empty.hidden = phase !== "empty";
    this.hungry.hidden = phase !== "hungry";
    this.full.hidden = phase !== "full";
    this.element.setAttribute("aria-valuenow", String(Math.max(0, this.energy)));
    this.element.setAttribute("aria-valuemax", String(ENERGY_DURATION_SECONDS));
    this.element.title = energyTooltip(this.energy);
  }

  private showTip(event: MouseEvent): void {
    this.tip.textContent = energyTooltip(this.energy);
    this.tip.style.left = `${event.clientX}px`;
    this.tip.style.top = `${event.clientY - 15}px`;
    this.tip.hidden = false;
  }
}
